import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import QuickActionButton from '../../components/ui/QuickActionButton';
import MatchStatusIndicator from '../../components/ui/MatchStatusIndicator';
import UserContextSwitcher from '../../components/ui/UserContextSwitcher';
import VenueMap from './components/VenueMap';
import VenueList from './components/VenueList';
import SearchFilters from './components/SearchFilters';
import BookingModal from './components/BookingModal';

import Button from '../../components/ui/Button';

const VenueSearch = () => {
  const navigate = useNavigate();
  const [venues, setVenues] = useState([]);
  const [filteredVenues, setFilteredVenues] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('split'); // 'map', 'list', 'split'
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingVenue, setBookingVenue] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: -23.5505, lng: -46.6333 }); // São Paulo default

  const [filters, setFilters] = useState({
    location: '',
    radius: 5,
    priceMin: 0,
    priceMax: 200,
    courtType: 'all',
    timeSlot: '',
    date: '',
    amenities: [],
    sortBy: 'distance',
    availableOnly: false
  });

  // Mock venue data
  const mockVenues = [
  {
    id: 1,
    name: 'Arena Sports Morumbi',
    address: 'Rua das Olimpíadas, 123 - Morumbi, São Paulo - SP',
    location: { lat: -23.5505, lng: -46.6333 },
    pricePerHour: 80,
    rating: 4.8,
    reviewCount: 127,
    courtType: 'Grama Sintética',
    capacity: 14,
    availability: 'available',
    distance: 1.2,
    images: [
    'https://images.pexels.com/photos/274422/pexels-photo-274422.jpeg',
    'https://images.pexels.com/photos/1171084/pexels-photo-1171084.jpeg',
    'https://images.pexels.com/photos/209977/pexels-photo-209977.jpeg'],

    imageAlts: [
    'Campo de futebol com grama sintética verde e traves brancas sob céu azul',
    'Vista aérea de quadra esportiva com marcações brancas e iluminação noturna',
    'Vestiário moderno com bancos de madeira e armários azuis'],

    amenities: [
    { name: 'Estacionamento', icon: 'Car' },
    { name: 'Vestiário', icon: 'Shirt' },
    { name: 'Chuveiro', icon: 'Droplets' },
    { name: 'Iluminação', icon: 'Lightbulb' },
    { name: 'Segurança', icon: 'Shield' }],

    nextAvailable: {
      date: 'Hoje',
      time: '18:00'
    },
    owner: {
      name: 'Carlos Silva',
      rating: 4.9
    }
  },
  {
    id: 2,
    name: 'Quadra do Parque Vila Madalena',
    address: 'Av. Paulista, 456 - Vila Madalena, São Paulo - SP',
    location: { lat: -23.5489, lng: -46.6388 },
    pricePerHour: 60,
    rating: 4.5,
    reviewCount: 89,
    courtType: 'Concreto',
    capacity: 10,
    availability: 'busy',
    distance: 2.1,
    images: [
    'https://images.pexels.com/photos/1171084/pexels-photo-1171084.jpeg',
    'https://images.pexels.com/photos/274422/pexels-photo-274422.jpeg'],

    imageAlts: [
    'Quadra de concreto com marcações amarelas e cestas de basquete',
    'Campo urbano cercado por prédios com grama natural bem cuidada'],

    amenities: [
    { name: 'Estacionamento', icon: 'Car' },
    { name: 'Iluminação', icon: 'Lightbulb' },
    { name: 'Wi-Fi', icon: 'Wifi' }],

    nextAvailable: {
      date: 'Amanhã',
      time: '14:00'
    },
    owner: {
      name: 'Ana Costa',
      rating: 4.7
    }
  },
  {
    id: 3,
    name: 'Centro Esportivo Ibirapuera',
    address: 'Parque Ibirapuera, 789 - Ibirapuera, São Paulo - SP',
    location: { lat: -23.5873, lng: -46.6573 },
    pricePerHour: 120,
    rating: 4.9,
    reviewCount: 203,
    courtType: 'Grama Natural',
    capacity: 22,
    availability: 'available',
    distance: 3.5,
    images: [
    'https://images.pexels.com/photos/209977/pexels-photo-209977.jpeg',
    'https://images.pexels.com/photos/274422/pexels-photo-274422.jpeg',
    'https://images.pexels.com/photos/1171084/pexels-photo-1171084.jpeg'],

    imageAlts: [
    'Campo profissional com grama natural verde e arquibancadas ao fundo',
    'Entrada moderna do centro esportivo com logo e paisagismo',
    'Área de aquecimento com equipamentos de treino e tatame azul'],

    amenities: [
    { name: 'Estacionamento', icon: 'Car' },
    { name: 'Vestiário', icon: 'Shirt' },
    { name: 'Chuveiro', icon: 'Droplets' },
    { name: 'Iluminação', icon: 'Lightbulb' },
    { name: 'Segurança', icon: 'Shield' },
    { name: 'Lanchonete', icon: 'Coffee' },
    { name: 'Aluguel de Equipamentos', icon: 'Package' }],

    nextAvailable: {
      date: 'Hoje',
      time: '20:00'
    },
    owner: {
      name: 'Roberto Santos',
      rating: 4.8
    }
  },
  {
    id: 4,
    name: 'Quadra Society Jardins',
    address: 'Rua Augusta, 321 - Jardins, São Paulo - SP',
    location: { lat: -23.5614, lng: -46.6565 },
    pricePerHour: 90,
    rating: 4.6,
    reviewCount: 156,
    courtType: 'Grama Sintética',
    capacity: 12,
    availability: 'available',
    distance: 1.8,
    images: [
    'https://images.pexels.com/photos/1171084/pexels-photo-1171084.jpeg',
    'https://images.pexels.com/photos/209977/pexels-photo-209977.jpeg'],

    imageAlts: [
    'Quadra society com grama sintética e redes nas laterais',
    'Vista noturna da quadra com iluminação LED e jogadores em ação'],

    amenities: [
    { name: 'Vestiário', icon: 'Shirt' },
    { name: 'Chuveiro', icon: 'Droplets' },
    { name: 'Iluminação', icon: 'Lightbulb' },
    { name: 'Wi-Fi', icon: 'Wifi' }],

    nextAvailable: {
      date: 'Hoje',
      time: '16:00'
    },
    owner: {
      name: 'Marcos Oliveira',
      rating: 4.5
    }
  },
  {
    id: 5,
    name: 'Arena Futsal Pinheiros',
    address: 'Rua dos Pinheiros, 654 - Pinheiros, São Paulo - SP',
    location: { lat: -23.5629, lng: -46.6825 },
    pricePerHour: 70,
    rating: 4.4,
    reviewCount: 98,
    courtType: 'Quadra Coberta',
    capacity: 10,
    availability: 'unavailable',
    distance: 2.7,
    images: [
    'https://images.pexels.com/photos/274422/pexels-photo-274422.jpeg'],

    imageAlts: [
    'Quadra coberta de futsal com piso sintético azul e iluminação artificial'],

    amenities: [
    { name: 'Estacionamento', icon: 'Car' },
    { name: 'Vestiário', icon: 'Shirt' },
    { name: 'Segurança', icon: 'Shield' },
    { name: 'Lanchonete', icon: 'Coffee' }],

    nextAvailable: {
      date: 'Segunda-feira',
      time: '19:00'
    },
    owner: {
      name: 'Fernanda Lima',
      rating: 4.6
    }
  }];


  // Initialize data and get user location
  useEffect(() => {
    const initializeData = async () => {
      setIsLoading(true);

      // Get user location
      if (navigator.geolocation) {
        navigator.geolocation?.getCurrentPosition(
          (position) => {
            const location = {
              lat: position?.coords?.latitude,
              lng: position?.coords?.longitude
            };
            setUserLocation(location);
            setMapCenter(location);
          },
          (error) => {
            console.log('Location access denied:', error);
          }
        );
      }

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));

      setVenues(mockVenues);
      setFilteredVenues(mockVenues);
      setIsLoading(false);
    };

    initializeData();
  }, []);

  // Filter venues based on current filters
  useEffect(() => {
    let filtered = [...venues];

    // Location filter (simplified - in real app would use geocoding)
    if (filters?.location) {
      filtered = filtered?.filter((venue) =>
      venue?.address?.toLowerCase()?.includes(filters?.location?.toLowerCase())
      );
    }

    // Price filter
    filtered = filtered?.filter((venue) =>
    venue?.pricePerHour >= filters?.priceMin && venue?.pricePerHour <= filters?.priceMax
    );

    // Court type filter
    if (filters?.courtType !== 'all') {
      const typeMap = {
        'grass': 'Grama Natural',
        'synthetic': 'Grama Sintética',
        'concrete': 'Concreto',
        'indoor': 'Quadra Coberta',
        'sand': 'Areia'
      };
      filtered = filtered?.filter((venue) => venue?.courtType === typeMap?.[filters?.courtType]);
    }

    // Availability filter
    if (filters?.availableOnly) {
      filtered = filtered?.filter((venue) => venue?.availability === 'available');
    }

    // Amenities filter
    if (filters?.amenities?.length > 0) {
      filtered = filtered?.filter((venue) =>
      filters?.amenities?.every((amenity) =>
      venue?.amenities?.some((venueAmenity) =>
      venueAmenity?.name?.toLowerCase()?.includes(amenity?.toLowerCase())
      )
      )
      );
    }

    setFilteredVenues(filtered);
  }, [filters, venues]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleFiltersReset = (resetFilters) => {
    setFilters(resetFilters);
  };

  const handleVenueSelect = (venue) => {
    setSelectedVenue(venue);
    setMapCenter(venue?.location);
  };

  const handleVenueBooking = (venue) => {
    setBookingVenue(venue);
    setShowBookingModal(true);
  };

  const handleBookingConfirm = (bookingData) => {
    console.log('Booking confirmed:', bookingData);
    // In real app, would send to API and redirect to confirmation page
    navigate('/dashboard');
  };

  const handleMapCenterChange = (newCenter) => {
    setMapCenter(newCenter);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container mx-auto px-4 py-6">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground">
              Buscar Quadras
            </h1>
            <p className="text-muted-foreground">Encontre e reserve a quadra perfeita para sua partida

            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            <MatchStatusIndicator />
            <UserContextSwitcher />
          </div>
        </div>

        {/* Search Filters */}
        <div className="mb-6">
          <SearchFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onReset={handleFiltersReset}
            isLoading={isLoading} />

        </div>

        {/* View Mode Controls - Mobile */}
        <div className="lg:hidden mb-4">
          <div className="flex border border-border rounded-lg p-1 bg-card">
            <Button
              variant={viewMode === 'map' ? "default" : "ghost"}
              size="sm"
              fullWidth
              onClick={() => setViewMode('map')}
              iconName="Map"
              iconPosition="left"
              iconSize={16}>

              Mapa
            </Button>
            <Button
              variant={viewMode === 'list' ? "default" : "ghost"}
              size="sm"
              fullWidth
              onClick={() => setViewMode('list')}
              iconName="List"
              iconPosition="left"
              iconSize={16}>

              Lista
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Map Section */}
          <div className={`${
          viewMode === 'list' ? 'hidden lg:block lg:col-span-5' :
          viewMode === 'map' ? 'col-span-1 lg:col-span-12' : 'hidden lg:block lg:col-span-5'}`
          }>
            <div className="sticky top-24">
              <div className="bg-card border border-border rounded-lg overflow-hidden" style={{ height: '600px' }}>
                <VenueMap
                  venues={filteredVenues}
                  selectedVenue={selectedVenue}
                  onVenueSelect={handleVenueSelect}
                  userLocation={userLocation}
                  mapCenter={mapCenter}
                  onMapCenterChange={handleMapCenterChange} />

              </div>
            </div>
          </div>

          {/* Venue List Section */}
          <div className={`${
          viewMode === 'map' ? 'hidden lg:block lg:col-span-7' :
          viewMode === 'list' ? 'col-span-1 lg:col-span-12' : 'col-span-1 lg:col-span-7'}`
          }>
            <VenueList
              venues={filteredVenues}
              onVenueSelect={handleVenueSelect}
              onVenueBooking={handleVenueBooking}
              selectedVenue={selectedVenue}
              isLoading={isLoading}
              filters={filters} />

          </div>
        </div>

        {/* View Mode Toggle - Desktop */}
        <div className="hidden lg:block fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
          <div className="bg-card border border-border rounded-lg shadow-elevation-3 p-1">
            <div className="flex items-center space-x-1">
              <Button
                variant={viewMode === 'split' ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode('split')}
                iconName="Columns"
                iconPosition="left"
                iconSize={16}>

                Dividido
              </Button>
              <Button
                variant={viewMode === 'map' ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode('map')}
                iconName="Map"
                iconPosition="left"
                iconSize={16}>

                Só Mapa
              </Button>
              <Button
                variant={viewMode === 'list' ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode('list')}
                iconName="List"
                iconPosition="left"
                iconSize={16}>

                Só Lista
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      <BookingModal
        venue={bookingVenue}
        isOpen={showBookingModal}
        onClose={() => {
          setShowBookingModal(false);
          setBookingVenue(null);
        }}
        onConfirm={handleBookingConfirm} />


      <QuickActionButton />
    </div>);

};

export default VenueSearch;